import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { Button, Image, Text } from '@change/ds-core-ui';
import { ArrowRightIcon, CheckCircleOutlineIcon, InstagramStickerIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

// Unshipped experiment copy (CHANGE-95456) — kept out of corgi.en-US.yml until a treatment productizes.
import I18N_PLACEHOLDERS from '#config/i18n/placeholders/psf/promoteOrShare/multiple-vertical-share-assets.yml';

import stickerLinkImage from '#src/assets/share/sticker_link.png';

import type { ShareAsset, ShareAssetSelection } from '../../../../../../shared/types';

import { ShareVisualCarousel } from './ShareVisualCarousel';
import { ShareVisualSwatches } from './ShareVisualSwatches';

/** Dropped in the asset-picker arms — the carousel replaces it. */
const StickerEducation: React.FC = () => {
	const { translate } = useI18n();

	return (
		<div className="flex flex-col gap-6">
			<div className="flex items-center justify-center gap-3">
				<InstagramStickerIcon className="drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]" size="x-large" />
				<ArrowRightIcon />
				<div>
					<Image src={stickerLinkImage} alt={translate('corgi.psf.share.modals.body.sticker_link_image_alt')} />
				</div>
			</div>

			<Text as="p" size="x-small" fontWeight="bold" variant="heading" className="px-10 text-center">
				<Translate value="corgi.psf.share.modals.body.description" />
			</Text>
		</div>
	);
};

/** The control layout: one static preview, unchanged from before the picker existed. */
const SinglePreview: React.FC<{ preview: File | string | null }> = ({ preview }) => {
	const { translate } = useI18n();
	const [imageUrl, setImageUrl] = useState<string | null>(null);

	useEffect(() => {
		if (preview instanceof File) {
			const url = URL.createObjectURL(preview);
			// FIXME this could be done with a useMemo, but it could cause issues on SSR due to the URL API
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setImageUrl(url);

			return () => {
				URL.revokeObjectURL(url);
			};
		}
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setImageUrl(preview);
		return undefined;
	}, [preview]);

	if (!imageUrl) return null;

	return (
		<div className="bg-background-raised rounded-lg p-4">
			<Image
				className="m-auto max-h-40 max-w-40 rounded-lg object-contain"
				aspectRatio="1:1"
				src={imageUrl}
				alt={translate('corgi.psf.share.modals.body.preview_image_alt')}
			/>
		</div>
	);
};

const EducationModalBodyComponent: React.FC<Props> = ({
	onContinue,
	shareVisualPreview,
	shareVisualPreviews,
	onAssetSelected,
	onActiveAssetChange,
	pickerLayout,
}) => {
	const { translate } = useI18n();
	const selectedIndexRef = useRef(0);
	// Whether the carousel was ever moved, which is not the same as ending on a non-zero position.
	const hasInteractedRef = useRef(false);
	const showPicker = !!shareVisualPreviews && shareVisualPreviews.length > 1;
	// Memoized: a fresh array each render would re-run the carousel's object-URL memo, minting new
	// blob URLs and revoking the ones the <img> tags are still pointing at.
	const previewFiles = useMemo(() => (shareVisualPreviews ?? []).map(({ file }) => file), [shareVisualPreviews]);
	const itemLabel = useCallback(
		(index: number) =>
			translate('corgi.psf.share.modals.body.asset_picker.item_label', {
				position: String(index + 1),
				total: String(previewFiles.length),
			}),
		[translate, previewFiles.length],
	);

	// Any tap counts as engaging with the picker, including one on the already-current asset that moves
	// nothing - see ShareVisualCarousel's onInteraction.
	const handleInteraction = useCallback(() => {
		// eslint-disable-next-line react-hooks/refs -- written on interaction, read on the Continue tap, never during render.
		hasInteractedRef.current = true;
		// A nudge that never reaches the next card leaves the index unchanged, so the callback below is
		// deduped away and never reports it. The position is unchanged by definition; what has to escape
		// this component is that the picker was used at all, before a close beats the settle debounce.
		// eslint-disable-next-line react-hooks/refs -- same.
		onActiveAssetChange?.(selectedIndexRef.current);
	}, [onActiveAssetChange]);

	// Separate from the tracking callback below: this one runs on every move, so the asset Continue ships
	// is never a debounce window behind what is centred on screen.
	const handleActiveIndexChange = useCallback(
		(selectedIndex: number) => {
			// eslint-disable-next-line react-hooks/refs -- written on interaction, read on the Continue tap, never during render.
			selectedIndexRef.current = selectedIndex;
			// eslint-disable-next-line react-hooks/refs -- same.
			hasInteractedRef.current = true;
			onActiveAssetChange?.(selectedIndex);
		},
		[onActiveAssetChange],
	);

	const handleSelectionChange = useCallback(
		(selectedIndex: number, interactionType: 'swipe' | 'tap') => {
			const asset = shareVisualPreviews?.[selectedIndex];
			if (!asset) return;

			onAssetSelected?.({
				variationName: asset.variationName,
				position: selectedIndex,
				total: shareVisualPreviews.length,
				interactionType,
			});
		},
		[onAssetSelected, shareVisualPreviews],
	);

	// `w-full` because the modal body lays out with `items-start`, so without it this subtree is
	// shrink-to-fit and the carousel's percentage centring padding resolves against a width derived from
	// its own content — circular — ballooning past the modal. Only the picker needs it, so control keeps
	// the markup production ships rather than inheriting a width fix for a layout it never renders.
	return (
		<div
			className={cn('mt-4 flex flex-col', showPicker && 'w-full')}
			aria-label={translate('fe.components.psf.instagram.modal_name')}
		>
			<div className="flex flex-col items-stretch gap-6 py-6">
				{!showPicker && <StickerEducation />}
				<div className="flex flex-col gap-4">
					{showPicker && (
						<AssetPicker
							layout={pickerLayout}
							assets={shareVisualPreviews}
							previews={previewFiles}
							label={translate('corgi.psf.share.modals.body.asset_picker.picker_label')}
							itemLabel={itemLabel}
							onActiveIndexChange={handleActiveIndexChange}
							onSelectionChange={handleSelectionChange}
							onInteraction={handleInteraction}
						/>
					)}
					{/* The carousel owns its own object URLs, so the single preview is never mounted alongside it. */}
					{!showPicker && <SinglePreview preview={shareVisualPreview} />}
					<div className="border-stroke-success-weak bg-fill-success-weaker mb-4 flex items-center justify-center gap-3 rounded-lg border p-6">
						<CheckCircleOutlineIcon />
						<Text size="large" className="text-typography-success font-semibold">
							{showPicker ? (
								<Translate value="corgi.psf.share.modals.body.asset_picker.link_copied_confirmation" />
							) : (
								<Translate value="corgi.psf.share.modals.body.link_copied_confirmation" />
							)}
						</Text>
					</div>
				</div>
			</div>
			<div className="border-stroke-weaker border-t py-4 pb-0">
				<Button
					variant="primary"
					onClick={async () => onContinue(selectedIndexRef.current, hasInteractedRef.current)}
					data-testid="instagram-stories-share-continue"
					data-qa="instagram-stories-share-continue"
					className="w-full"
				>
					{showPicker ? (
						<Translate value="corgi.psf.share.modals.body.asset_picker.button" />
					) : (
						<Translate value="corgi.psf.share.modals.body.button" />
					)}
				</Button>
			</div>
		</div>
	);
};

export const EducationModalBody = withI18nPlaceholders(I18N_PLACEHOLDERS, EducationModalBodyComponent);

/** Which picker the arm asked for. Both take the same callbacks, so only the presentation differs. */
const AssetPicker: React.FC<{
	layout: 'carousel' | 'swatches' | undefined;
	assets: readonly ShareAsset[];
	previews: readonly File[];
	label: string;
	itemLabel: (index: number) => string;
	onActiveIndexChange: (index: number) => void;
	onSelectionChange: (index: number, interactionType: 'swipe' | 'tap') => void;
	onInteraction: () => void;
}> = ({ layout, assets, previews, ...callbacks }) =>
	layout === 'swatches' ? (
		<ShareVisualSwatches assets={assets} {...callbacks} />
	) : (
		<ShareVisualCarousel previews={previews} {...callbacks} />
	);

type Props = {
	onContinue: (selectedIndex: number, hasInteracted: boolean) => Promise<void>;
	shareVisualPreview: File | string | null;
	/**
	 * Present only in the asset-picker arms (CHANGE-95456). When set, the carousel replaces the single
	 * preview and the educational sticker section is dropped — confirmed with design 2026-08-19.
	 */
	shareVisualPreviews?: readonly ShareAsset[];
	onAssetSelected?: (selection: ShareAssetSelection) => void;
	/**
	 * Fired on every move, undebounced, so the caller's retained tracking properties match what is on
	 * screen the moment it happens. `onAssetSelected` cannot serve this: it is debounced, and closing the
	 * modal resolves the share promise before that timer fires.
	 */
	onActiveAssetChange?: (selectedIndex: number) => void;
	/** Which picker to render. Absent falls back to the carousel, which is variant 1. */
	pickerLayout?: 'carousel' | 'swatches';
};
